import React, { useState, useRef } from 'react';
import { Row, Col, Form, Button, Card, Spinner, Modal } from 'react-bootstrap';
import { Formik, Field } from 'formik';
import Select from 'react-select';
import { Country, State, City } from 'country-state-city';
import * as Yup from 'yup';
import { useNavigate } from 'react-router-dom';

import PhoneInput from 'react-phone-number-input';
import { isValidPhoneNumber } from 'libphonenumber-js';
import 'react-phone-number-input/style.css';

import axiosInstance from '../../utils/axios';
import Wrapper from '../../components/Wrapper';
import SoilDoctorLogo from '../../assets/Images/SoilDoctorLogo.png';
import { PARTNER_CATEGORIES } from '../../config/permissions.config';

// --- Schema ---
const schema = Yup.object({
  companyName: Yup.string().required('Company name is required'),
  category: Yup.string().oneOf(['', ...PARTNER_CATEGORIES.map((c) => c.value)], 'Invalid category'),
  primaryPersonName: Yup.string().required('Primary person name is required'),
  primaryPersonMobile: Yup.string()
    .required('Primary person mobile is required')
    .test('is-valid-phone', 'Invalid Mobile number', (value) => 
      value ? isValidPhoneNumber(value) : false
    ),
  primaryPersonEmail: Yup.string()
    .email('Invalid email address')
    .required('Primary person email is required'),
  secondaryPersonName: Yup.string(),
  secondaryPersonMobile: Yup.string()
    .nullable()
    .test('is-valid-phone', 'Invalid Mobile number', (value) => {
      if (!value) return true;
      return isValidPhoneNumber(value);
    }),
  secondaryPersonEmail: Yup.string().email('Invalid email address').nullable(),
  address: Yup.string().required('Address is required'),
  
  pincode: Yup.string()
    .required('Zip/Postal Code is required')
    .matches(/^[a-zA-Z0-9\s\-]{3,12}$/, 'Invalid Zip/Postal Code'),
    
  country: Yup.string().required('Country is required'),
  state: Yup.string().required('State is required'),
  city: Yup.string().required('City is required'),

  wantsClinic: Yup.boolean(),
  clinic_code: Yup.string().when('wantsClinic', {
    is: true,
    then: (schema) => schema.required('Clinic Code is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  customer_type: Yup.string().when('wantsClinic', {
    is: true,
    then: (schema) => schema.required('Customer Type is required'),
    otherwise: (schema) => schema.notRequired(),
  }),
  custom_customer_type: Yup.string().when(['wantsClinic', 'customer_type'], {
    is: (wantsClinic, customer_type) => wantsClinic && customer_type === 'Other',
    then: (schema) => schema.required('Please specify the customer type'),
    otherwise: (schema) => schema.notRequired(),
  }),
  clinic_latitude: Yup.number().typeError('Must be a number').when('wantsClinic', {
    is: true,
    then: (schema) => schema.required('Latitude is required'),
    otherwise: (schema) => schema.nullable(),
  }),
  clinic_longitude: Yup.number().typeError('Must be a number').when('wantsClinic', {
    is: true,
    then: (schema) => schema.required('Longitude is required'),
    otherwise: (schema) => schema.nullable(),
  }),
});

const generateClassName = (errors, touched, fieldName) => {
  const hasError = errors[fieldName] && touched[fieldName];
  const isSuccess = touched[fieldName];
  return `form-control ${
    hasError ? 'border-2 border-danger' : isSuccess ? 'border-2 border-success' : ''
  }`;
};

const generatePhoneClassName = (errors, touched, fieldName) => {
  const hasError = errors[fieldName] && touched[fieldName];
  const isSuccess = touched[fieldName];
  return `d-flex align-items-center form-control ${
    hasError ? 'border-2 border-danger' : isSuccess ? 'border-2 border-success' : ''
  }`;
};

function AddPartner() {
  const navigate = useNavigate();
  
  const stateSelectRef = useRef(null);
  const citySelectRef = useRef(null);
  
  const [countryCode, setCountryCode] = useState('');
  const [stateCode, setStateCode] = useState('');
  
  const [isLoading, setIsLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [partnerCreds, setPartnerCreds] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const updatedCountries = () =>
    Country.getAllCountries().map((country) => ({
      label: country.name,
      value: country.isoCode,
      ...country,
    }));

  const updatedStates = (isoCountryCode) =>
    isoCountryCode ? State.getStatesOfCountry(isoCountryCode).map((state) => ({
      label: state.name,
      value: state.isoCode,
      ...state,
    })) : [];

  const updatedCities = (isoCountryCode, isoStateCode) =>
    isoCountryCode && isoStateCode ? City.getCitiesOfState(isoCountryCode, isoStateCode).map((city) => ({
      label: city.name,
      value: city.name,
      ...city,
    })) : [];

  const handleClose = () => {
    setShowModal(false);
    navigate('/'); 
  };

  return (
    <Wrapper>
      <div className="py-4">
        <h2 className="fw-bold mb-4">Add New Partner</h2>
        
        <Card className="border-0 shadow-sm rounded-4">
          <Card.Body className="p-4 p-md-5">
            <Formik
              initialValues={{
                companyName: '',
                primaryPersonName: '',
                primaryPersonMobile: '',
                primaryPersonEmail: '',
                secondaryPersonName: '',
                secondaryPersonMobile: '',
                secondaryPersonEmail: '',
                address: '',
                pincode: '',
                country: '',
                state: '',
                city: '',
                category: '',
                wantsClinic: false,
                clinic_code: '',
                customer_type: '',
                custom_customer_type: '',
                clinic_status: 'FUNCTIONAL',
                clinic_latitude: '',
                clinic_longitude: '',
                clinic_about: ''
              }}
              validationSchema={schema}
              onSubmit={async (values, { setSubmitting, resetForm }) => {
                setIsLoading(true);
                setErrorMsg('');
                
                try {
                  const inquiryRes = await axiosInstance.post('/Inquiry', {
                    entity_name: values.companyName,
                    primary_person_name: values.primaryPersonName,
                    primary_person_mobile_no: values.primaryPersonMobile,
                    primary_person_email_id: values.primaryPersonEmail,
                    secondary_person_name: values.secondaryPersonName,
                    secondary_person_mobile_no: values.secondaryPersonMobile,
                    secondary_person_email_id: values.secondaryPersonEmail,
                    business_address: values.address,
                    pincode: values.pincode,
                    city: values.city,
                    state: values.state,
                    source: 'operations',
                  });

                  if (inquiryRes.data.inquiry_id) {
                    const adminRes = await axiosInstance.post('/super-admin/AddAdmin', {
                      inquiry_id: inquiryRes.data.inquiry_id,
                      category: values.category || null,
                    });

                    const adminData = adminRes.data;
                    let rawAdmin = null;

                    if (Array.isArray(adminData) && adminData.length > 0) {
                        rawAdmin = adminData[0];
                    } else if (adminData && adminData.username) {
                        rawAdmin = adminData;
                    }

                    if (rawAdmin) {
                        // --- Added Clinic Creation ---
                        if (values.wantsClinic) {
                          const adminIdToUse = rawAdmin.admin_id || rawAdmin.id || rawAdmin._id;
                          if (adminIdToUse) {
                            await axiosInstance.post('/super-admin/clinics', {
                              clinic_code: values.clinic_code,
                              admin_id: adminIdToUse,
                              customer_type: values.customer_type === 'Other' ? values.custom_customer_type : values.customer_type,
                              state: values.state,
                              district: values.city,
                              status: values.clinic_status,
                              contact: values.primaryPersonMobile,
                              latitude: values.clinic_latitude,
                              longitude: values.clinic_longitude,
                              address: values.address,
                              pincode: values.pincode,
                              about: values.clinic_about,
                            });
                          }
                        }

                        setPartnerCreds({
                            username: rawAdmin.username,
                            password: rawAdmin.password,
                            clinicCreated: values.wantsClinic // Added flag for modal
                        });

                        setShowModal(true);
                        resetForm();
                        setCountryCode('');
                        setStateCode('');
                        if(stateSelectRef.current) stateSelectRef.current.clearValue();
                        if(citySelectRef.current) citySelectRef.current.clearValue();
                    } else {
                        throw new Error('Inquiry created, but failed to generate admin credentials.');
                    }
                  }
                } catch (error) {
                  console.error('Add Partner Error:', error);
                  setErrorMsg(error.response?.data?.msg || error.message || 'Operation failed. Please try again.');
                } finally {
                  setIsLoading(false);
                  setSubmitting(false);
                }
              }}
            >
              {({
                values,
                handleChange,
                handleSubmit,
                setFieldValue,
                setFieldTouched,
                errors,
                touched,
              }) => (
                <Form onSubmit={handleSubmit}>
                  {errorMsg && (
                    <div className="alert alert-danger mb-4" role="alert">
                      {errorMsg}
                    </div>
                  )}

                  <h5 className="mb-3 text-muted">Company Details</h5>
                  <Row className="mb-4 g-3">
                    <Col md={8}>
                      <div className="form-floating">
                        <Field
                          name="companyName"
                          placeholder="Company Name"
                          className={generateClassName(errors, touched, 'companyName')}
                          onChange={handleChange}
                        />
                        <Form.Label>Company Name <span className="text-danger">*</span></Form.Label>
                      </div>
                      {errors.companyName && touched.companyName && (
                        <div className="text-danger small mt-1">&#9888; {errors.companyName}</div>
                      )}
                    </Col>
                    <Col md={4}>
                      <div className="form-floating">
                        <Field
                          as="select"
                          name="category"
                          className="form-select"
                          onChange={handleChange}
                        >
                          <option value="">Uncategorized</option>
                          {PARTNER_CATEGORIES.map(({ value, label }) => (
                            <option key={value} value={value}>{label}</option>
                          ))}
                        </Field>
                        <Form.Label>Category</Form.Label>
                      </div>
                      {errors.category && touched.category && (
                        <div className="text-danger small mt-1">&#9888; {errors.category}</div>
                      )}
                    </Col>
                  </Row>

                  <h5 className="mb-3 text-muted">Primary Contact</h5>
                  <Row className="mb-4 g-3">
                    <Col md={4}>
                      <div className="form-floating">
                        <Field
                          name="primaryPersonName"
                          placeholder="Name"
                          className={generateClassName(errors, touched, 'primaryPersonName')}
                          onChange={handleChange}
                        />
                        <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                      </div>
                      {errors.primaryPersonName && touched.primaryPersonName && (
                        <div className="text-danger small mt-1">&#9888; {errors.primaryPersonName}</div>
                      )}
                    </Col>
                    <Col md={4}>
                      <div>
                        <Field name="primaryPersonMobile">
                          {({ field, form }) => (
                            <PhoneInput
                              international
                              defaultCountry="IN"
                              placeholder="Mobile *"
                              value={field.value}
                              onChange={(val) => {
                                form.setFieldValue(field.name, val);
                                form.setFieldTouched(field.name, true, false);
                              }}
                              onBlur={() => form.setFieldTouched(field.name, true)}
                              className={generatePhoneClassName(errors, touched, 'primaryPersonMobile')}
                            />
                          )}
                        </Field>
                      </div>
                      {errors.primaryPersonMobile && touched.primaryPersonMobile && (
                        <div className="text-danger small mt-1">&#9888; {errors.primaryPersonMobile}</div>
                      )}
                    </Col>
                    <Col md={4}>
                      <div className="form-floating">
                        <Field
                          name="primaryPersonEmail"
                          placeholder="Email"
                          className={generateClassName(errors, touched, 'primaryPersonEmail')}
                          onChange={handleChange}
                        />
                        <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                      </div>
                      {errors.primaryPersonEmail && touched.primaryPersonEmail && (
                        <div className="text-danger small mt-1">&#9888; {errors.primaryPersonEmail}</div>
                      )}
                    </Col>
                  </Row>

                  <h5 className="mb-3 text-muted">Secondary Contact (Optional)</h5>
                  <Row className="mb-4 g-3">
                    <Col md={4}>
                      <div className="form-floating">
                        <Field
                          name="secondaryPersonName"
                          placeholder="Name"
                          className={generateClassName(errors, touched, 'secondaryPersonName')}
                          onChange={handleChange}
                        />
                        <Form.Label>Name</Form.Label>
                      </div>
                    </Col>
                    <Col md={4}>
                      <div>
                        <Field name="secondaryPersonMobile">
                          {({ field, form }) => (
                            <PhoneInput
                              international
                              defaultCountry="IN"
                              placeholder="Mobile"
                              value={field.value}
                              onChange={(val) => {
                                form.setFieldValue(field.name, val);
                                form.setFieldTouched(field.name, true, false);
                              }}
                              onBlur={() => form.setFieldTouched(field.name, true)}
                              className={generatePhoneClassName(errors, touched, 'secondaryPersonMobile')}
                            />
                          )}
                        </Field>
                      </div>
                      {errors.secondaryPersonMobile && touched.secondaryPersonMobile && (
                        <div className="text-danger small mt-1">&#9888; {errors.secondaryPersonMobile}</div>
                      )}
                    </Col>
                    <Col md={4}>
                      <div className="form-floating">
                        <Field
                          name="secondaryPersonEmail"
                          placeholder="Email"
                          className={generateClassName(errors, touched, 'secondaryPersonEmail')}
                          onChange={handleChange}
                        />
                        <Form.Label>Email</Form.Label>
                      </div>
                      {errors.secondaryPersonEmail && touched.secondaryPersonEmail && (
                        <div className="text-danger small mt-1">&#9888; {errors.secondaryPersonEmail}</div>
                      )}
                    </Col>
                  </Row>

                  <h5 className="mb-3 text-muted">Location</h5>
                  <Row className="mb-4 g-3">
                    <Col md={12}>
                      <div className="form-floating">
                        <Field
                          name="address"
                          placeholder="Address"
                          className={generateClassName(errors, touched, 'address')}
                          onChange={handleChange}
                        />
                        <Form.Label>Address <span className="text-danger">*</span></Form.Label>
                      </div>
                      {errors.address && touched.address && (
                        <div className="text-danger small mt-1">&#9888; {errors.address}</div>
                      )}
                    </Col>
                    
                    {/* Country Dropdown */}
                    <Col md={4}>
                      <Select
                        name="country"
                        placeholder="Select Country"
                        classNamePrefix="react-select"
                        options={updatedCountries()}
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 10 }),
                          menu: (base) => ({ ...base, zIndex: 10 }),
                        }}
                        onChange={(opt) => {
                          setFieldValue('country', opt ? opt.label : '');
                          setCountryCode(opt ? opt.value : '');
                          setFieldValue('state', '');
                          setStateCode('');
                          setFieldValue('city', '');
                          if(stateSelectRef.current) stateSelectRef.current.clearValue();
                          if(citySelectRef.current) citySelectRef.current.clearValue();
                        }}
                        onBlur={() => setFieldTouched('country', true)}
                        className={errors.country && touched.country ? 'border border-danger rounded' : ''}
                      />
                      {errors.country && touched.country && <div className="text-danger small mt-1">&#9888; {errors.country}</div>}
                    </Col>

                    {/* State Dropdown */}
                    <Col md={4}>
                      <Select
                        ref={stateSelectRef}
                        name="state"
                        placeholder="Select State"
                        classNamePrefix="react-select"
                        options={updatedStates(countryCode)}
                        isDisabled={!countryCode}
                        onChange={(opt) => {
                          setFieldValue('state', opt ? opt.label : '');
                          setStateCode(opt ? opt.value : '');
                          
                          setFieldValue('city', '');
                          if(citySelectRef.current) citySelectRef.current.clearValue();
                        }}
                        onBlur={() => setFieldTouched('state', true)}
                        className={errors.state && touched.state ? 'border border-danger rounded' : ''}
                      />
                      {errors.state && touched.state && <div className="text-danger small mt-1">&#9888; {errors.state}</div>}
                    </Col>

                    {/* City Dropdown */}
                    <Col md={4}>
                      <Select
                        ref={citySelectRef}
                        name="city"
                        placeholder="Select City"
                        classNamePrefix="react-select"
                        options={updatedCities(countryCode, stateCode)}
                        isDisabled={!stateCode}
                        onChange={(opt) => setFieldValue('city', opt ? opt.label : '')}
                        onBlur={() => setFieldTouched('city', true)}
                        className={errors.city && touched.city ? 'border border-danger rounded' : ''}
                      />
                      {errors.city && touched.city && <div className="text-danger small mt-1">&#9888; {errors.city}</div>}
                    </Col>

                    {/* Zip / Pincode */}
                    <Col md={4}>
                      <div className="form-floating">
                        <Field
                          name="pincode"
                          placeholder="Zip / Postal Code"
                          className={generateClassName(errors, touched, 'pincode')}
                          onChange={handleChange}
                        />
                        <Form.Label>Zip / Postal Code <span className="text-danger">*</span></Form.Label>
                      </div>
                      {errors.pincode && touched.pincode && (
                        <div className="text-danger small mt-1">&#9888; {errors.pincode}</div>
                      )}
                    </Col>
                  </Row>

                  {/* --- Added Clinic Section --- */}
                  <div className="mb-4 mt-4 pt-4 border-top">
                    <Form.Check 
                      type="switch"
                      id="clinic-switch"
                      label={<span className="fw-bold ms-2">Does this partner want a Soil Doctor Clinic?</span>}
                      checked={values.wantsClinic}
                      onChange={(e) => setFieldValue('wantsClinic', e.target.checked)}
                    />
                  </div>

                  {values.wantsClinic && (
                    <div className="bg-light p-4 rounded-3 border mb-4">
                      <h5 className="mb-3 text-muted">Clinic Details</h5>
                      <p className="text-muted small mb-4">
                        The clinic will automatically inherit the partner's Contact, State, City, Address, and Pincode.
                      </p>
                      
                      <Row className="g-3">
                        <Col md={6}>
                          <div className="form-floating">
                            <Field name="clinic_code" placeholder="Clinic Code" className={generateClassName(errors, touched, 'clinic_code')} onChange={handleChange} />
                            <Form.Label>Clinic Code <span className="text-danger">*</span></Form.Label>
                          </div>
                          {errors.clinic_code && touched.clinic_code && <div className="text-danger small mt-1">&#9888; {errors.clinic_code}</div>}
                        </Col>
                        
                        <Col md={6}>
                          <div className="form-floating">
                            <Field 
                              as="select" 
                              name="customer_type" 
                              className={generateClassName(errors, touched, 'customer_type')}
                              onChange={(e) => {
                                setFieldValue('customer_type', e.target.value);
                                if (e.target.value !== 'Other') setFieldValue('custom_customer_type', '');
                              }}
                            >
                              <option value="">Select Customer Type</option>
                              <option value="Private Company">Private Company</option>
                              <option value="Agri Retailer / Krishi Kendra">Agri Retailer / Krishi Kendra</option>
                              <option value="FPC / FPO">FPC / FPO</option>
                              <option value="NGO">NGO</option>
                              <option value="Individual / Entrepreneur">Individual / Entrepreneur</option>
                              <option value="Foundation">Foundation</option>
                              <option value="Corporate CSR">Corporate CSR</option>
                              <option value="Other">Other (Please Specify)</option>
                            </Field>
                            <Form.Label>Customer Type <span className="text-danger">*</span></Form.Label>
                          </div>
                          {errors.customer_type && touched.customer_type && <div className="text-danger small mt-1">&#9888; {errors.customer_type}</div>}
                        </Col>

                        {values.customer_type === 'Other' && (
                          <Col md={12}>
                            <div className="form-floating">
                              <Field name="custom_customer_type" placeholder="Specify Customer Type" className={generateClassName(errors, touched, 'custom_customer_type')} onChange={handleChange} />
                              <Form.Label>Specify Customer Type <span className="text-danger">*</span></Form.Label>
                            </div>
                            {errors.custom_customer_type && touched.custom_customer_type && <div className="text-danger small mt-1">&#9888; {errors.custom_customer_type}</div>}
                          </Col>
                        )}

                        <Col md={4}>
                          <div className="form-floating">
                            <Field as="select" name="clinic_status" className="form-select" onChange={handleChange}>
                              <option value="FUNCTIONAL">Functional</option>
                              <option value="NON_FUNCTIONAL">Non-Functional</option>
                            </Field>
                            <Form.Label>Status</Form.Label>
                          </div>
                        </Col>

                        <Col md={4}>
                          <div className="form-floating">
                            <Field type="number" step="any" name="clinic_latitude" placeholder="Latitude" className={generateClassName(errors, touched, 'clinic_latitude')} onChange={handleChange} />
                            <Form.Label>Latitude <span className="text-danger">*</span></Form.Label>
                          </div>
                          {errors.clinic_latitude && touched.clinic_latitude && <div className="text-danger small mt-1">&#9888; {errors.clinic_latitude}</div>}
                        </Col>

                        <Col md={4}>
                          <div className="form-floating">
                            <Field type="number" step="any" name="clinic_longitude" placeholder="Longitude" className={generateClassName(errors, touched, 'clinic_longitude')} onChange={handleChange} />
                            <Form.Label>Longitude <span className="text-danger">*</span></Form.Label>
                          </div>
                          {errors.clinic_longitude && touched.clinic_longitude && <div className="text-danger small mt-1">&#9888; {errors.clinic_longitude}</div>}
                        </Col>

                        <Col md={12}>
                          <div className="form-floating">
                            <Field as="textarea" name="clinic_about" style={{ height: '80px' }} placeholder="About Clinic" className="form-control" onChange={handleChange} />
                            <Form.Label>About Clinic (Optional)</Form.Label>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  )}
                  {/* --- End Added Clinic Section --- */}

                  <div className="d-flex justify-content-end mt-4">
                    <Button 
                        type="submit" 
                        variant="dark" 
                        size="lg" 
                        disabled={isLoading}
                        className="px-5"
                    >
                      {isLoading ? <Spinner as="span" animation="border" size="sm" /> : 'Submit'}
                    </Button>
                  </div>
                </Form>
              )}
            </Formik>
          </Card.Body>
        </Card>

        {/* --- CREDENTIALS MODAL --- */}
        <Modal show={showModal} backdrop="static" centered>
            <Modal.Header>
              <Modal.Title className="d-flex align-items-center gap-2">
                <img src={SoilDoctorLogo} alt="logo" width="120" />
              </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-4">
              <h4 className="mb-3 text-success">
                Partner {partnerCreds?.clinicCreated && '& Clinic'} Created Successfully!
              </h4>
              <p className="text-muted">
                An Admin account has been created for the new partner. 
                Please share the following credentials securely:
              </p>
              
              <div className="bg-light p-3 rounded border mb-3">
                <div className="mb-2">
                    <span className="fw-bold text-secondary">Username/Email:</span> <br/>
                    <span className="user-select-all fs-5">{partnerCreds?.username || 'N/A'}</span>
                </div>
                <div>
                    <span className="fw-bold text-secondary">Password:</span> <br/>
                    <span className="user-select-all fs-5 text-dark fw-bold">{partnerCreds?.password || 'N/A'}</span>
                </div>
              </div>

              <div className="form-check mt-3">
                <input
                    className="form-check-input"
                    type="checkbox"
                    id="ackCheck"
                    checked={isChecked}
                    onChange={() => setIsChecked(!isChecked)}
                /> 
                <label className="form-check-label" htmlFor="ackCheck">
                    I have noted down the credentials.
                </label>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button
                variant="dark"
                disabled={!isChecked}
                onClick={handleClose}
              >
                Close
              </Button>
            </Modal.Footer>
        </Modal>

      </div>
    </Wrapper>
  );
}

export default AddPartner;